// Insurance Add-on Manager - Clean Refactored Version
(function () {
    'use strict';

    // ==========================================
    // Configuration
    // ==========================================
    const CONFIG = {
        selectors: {
            smartCart: '.rebuy-cart', // Parent cart container
            cartAddon: '#sca-cart-addon',
            addOnTitle: '.product-title-sca',
            insuranceCheckbox: '#sca-cart-addon input[type="checkbox"]',
            modal: '#cs-modal-1605',
            closeButton: '#cs-close-1605',
            infoButton: '#cs-info-button-1605'
        },
        observerTargets: {
            // More specific targets to watch instead of entire body
            cartContainer: '.rebuy-cart__flyout',
            fallbackContainer: 'body'
        },
        delays: {
            initialization: 1000,
            checkboxCheck: 50,
            stabilization: 100, // Reduced from 300ms to 100ms for faster response
            update: 1
        }
    };

    // ==========================================
    // State Management
    // ==========================================
    class StateManager {
        constructor() {
            this.state = {
                observer: null,
                isProcessing: false,
                hasInitialized: false,
                modalReady: false,
                lastProcessedTimestamp: 0,
                addonPresent: false
            };
        }

        startProcessing() {
            if (this.state.isProcessing) return false;
            this.state.isProcessing = true;
            this.state.lastProcessedTimestamp = Date.now();
            return true;
        }

        stopProcessing() {
            setTimeout(() => {
                this.state.isProcessing = false;
            }, CONFIG.delays.stabilization);
        }

        shouldProcess() {
            const timeSinceLastProcess = Date.now() - this.state.lastProcessedTimestamp;
            return !this.state.isProcessing && timeSinceLastProcess > CONFIG.delays.stabilization;
        }
    }

    const stateManager = new StateManager();

    // ==========================================
    // DOM Utilities
    // ==========================================
    const DOMUtils = {
        querySelector(selector, parent = document) {
            try {
                return parent.querySelector(selector);
            } catch (e) {
                console.error(`Invalid selector: ${selector}`, e);
                return null;
            }
        },

        waitForElement(selector, timeout = 5000) {
            return new Promise((resolve) => {
                const element = this.querySelector(selector);
                if (element) {
                    resolve(element);
                    return;
                }

                const observer = new MutationObserver(() => {
                    const element = this.querySelector(selector);
                    if (element) {
                        observer.disconnect();
                        resolve(element);
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true
                });

                setTimeout(() => {
                    observer.disconnect();
                    resolve(null);
                }, timeout);
            });
        },

        isDescendantOf(child, parentSelector) {
            return child && child.closest(parentSelector) !== null;
        }
    };

    // ==========================================
    // Insurance Button Component
    // ==========================================
    class InsuranceButton {
        constructor() {
            this.buttonId = CONFIG.selectors.infoButton.replace('#', '');
        }

        create() {
            const button = document.createElement('button');
            button.id = this.buttonId;
            button.className = 'information-button';
            button.setAttribute('role', 'button');
            button.setAttribute('aria-label', 'Insurance Information');
            button.setAttribute('tabindex', '0');

            // Styles for better touch interaction
            Object.assign(button.style, {
                cursor: 'pointer',
                touchAction: 'manipulation',
                webkitTapHighlightColor: 'transparent',
                userSelect: 'none'
            });

            button.innerHTML = this._getSVGIcon();
            return button;
        }

        add(titleElement) {
            if (!titleElement) return false;

            // Check if button already exists in the correct location
            const existingButton = titleElement.querySelector(`#${this.buttonId}`);
            if (existingButton) {
                console.log('✓ Info button already in place');
                return false;
            }

            // Remove any orphaned buttons
            const orphanedButtons = document.querySelectorAll(`#${this.buttonId}`);
            if (orphanedButtons.length > 0) {
                console.log(`🧹 Removing ${orphanedButtons.length} orphaned button(s)`);
                orphanedButtons.forEach(btn => btn.remove());
            }

            // Add new button
            const button = this.create();
            titleElement.appendChild(button);
            console.log('✓ Info button added to:', titleElement.className || 'element');
            return true;
        }

        _getSVGIcon() {
            return `
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" 
                     viewBox="0 0 512 512" width="20" height="20">
                    <g>
                        <path d="M437.02,74.98C388.667,26.628,324.381,0,256,0C187.62,0,123.333,26.628,74.98,74.98C26.628,123.333,0,187.619,0,256s26.628,132.667,74.98,181.02C123.333,485.372,187.62,512,256,512c68.381,0,132.667-26.628,181.02-74.98S512,324.381,512,256S485.372,123.333,437.02,74.98z M256,478.609c-122.746,0-222.609-99.862-222.609-222.609S133.254,33.391,256,33.391S478.609,133.254,478.609,256S378.746,478.609,256,478.609z" 
                              fill="#003057"/>
                        <rect x="231.58" y="203.776" width="49.208" height="192.412" fill="#003057"/>
                        <rect x="231.58" y="128.134" width="49.208" height="48.839" fill="#003057"/>
                    </g>
                </svg>`;
        }
    }

    // ==========================================
    // Modal Controller
    // ==========================================
    class ModalController {
        constructor() {
            this.modal = null;
            this.closeButton = null;
            this.isInitialized = false;
        }

        async initialize() {
            if (this.isInitialized) return true;

            this.modal = DOMUtils.querySelector(CONFIG.selectors.modal);
            this.closeButton = DOMUtils.querySelector(CONFIG.selectors.closeButton);

            if (!this.modal || !this.closeButton) {
                console.log('⏳ Modal elements not ready');
                return false;
            }

            this._setupModal();
            this._attachEventListeners();
            this.isInitialized = true;
            console.log('✓ Modal initialized');
            return true;
        }

        _setupModal() {
            this.modal.classList.add('cs-closed');
            this.modal.classList.remove('cs-loaded');
            this.modal.style.zIndex = '-1000';
        }

        _attachEventListeners() {
            // Use event delegation for dynamic elements
            document.addEventListener('click', this._handleClick.bind(this), { passive: false });
            document.addEventListener('touchend', this._handleTouch.bind(this), { passive: false });
        }

        _handleClick(e) {
            const target = e.target;

            // Check if info button was clicked
            if (target.closest(CONFIG.selectors.infoButton)) {
                e.preventDefault();
                e.stopPropagation();
                this.show();
                return;
            }

            // Check if close button or outside modal was clicked
            if (target.closest(CONFIG.selectors.closeButton) || target === this.modal) {
                e.preventDefault();
                this.hide();
            }
        }

        _handleTouch(e) {
            if (e.target.closest(CONFIG.selectors.infoButton)) {
                e.preventDefault();
                this.show();
            }
        }

        show() {
            if (!this.modal) return;
            this.modal.classList.remove('cs-closed');
            this.modal.classList.add('cs-loaded');
            this.modal.style.zIndex = '999999999999999999999';
            document.body.style.overflow = 'hidden';
        }

        hide() {
            if (!this.modal) return;
            this.modal.classList.add('cs-closed');
            this.modal.classList.remove('cs-loaded');
            this.modal.style.zIndex = '-1000';
            document.body.style.overflow = '';
        }
    }

    // ==========================================
    // Insurance Checkbox Controller
    // ==========================================
    class InsuranceCheckbox {
        static check() {
            const checkbox = DOMUtils.querySelector(CONFIG.selectors.insuranceCheckbox);

            if (!checkbox) {
                console.log('⚠ Checkbox not found');
                return false;
            }

            if (checkbox.checked) {
                console.log('✓ Checkbox already checked');
                return false;
            }

            // Check the checkbox
            checkbox.checked = true;
            return true;

            console.log('✓ Checkbox checked');
        }
    }

    // ==========================================
    // Main Add-on Manager
    // ==========================================
    class InsuranceAddonManager {
        constructor() {
            this.button = new InsuranceButton();
            this.modal = new ModalController();
            this.observer = null;
        }

        async initialize() {
            if (stateManager.state.hasInitialized) {
                console.log('Already initialized');
                return;
            }

            console.log('🚀 Initializing Insurance Add-on Manager');

            // Initialize modal
            await this.modal.initialize();

            // Setup observer
            this.setupObserver();

            // Process current state
            await this.processAddon();

            stateManager.state.hasInitialized = true;
            console.log('✅ Insurance Add-on Manager ready');
        }

        setupObserver() {
            // Disconnect existing observer if any
            if (this.observer) {
                this.observer.disconnect();
            }

            // Find the most specific container to observe
            const targetNode = DOMUtils.querySelector(CONFIG.observerTargets.cartContainer) ||
                document.body;

            this.observer = new MutationObserver(this.handleMutations.bind(this));

            this.observer.observe(targetNode, {
                childList: true,
                subtree: true,
                attributes: false // We don't need attribute changes
            });

            console.log('✓ Observer attached to:', targetNode.className || 'body');
        }

        handleMutations(mutations) {
            // Quick check if we should process
            if (!stateManager.shouldProcess()) {
                return;
            }

            // Check if mutations are relevant to our addon
            const relevantMutation = mutations.some(mutation => {
                // Check added nodes
                return Array.from(mutation.addedNodes).some(node => {
                    if (node.nodeType !== Node.ELEMENT_NODE) return false;

                    // Check if the node or its children contain our addon
                    return node.id === 'sca-cart-addon' ||
                        node.querySelector?.(CONFIG.selectors.cartAddon) ||
                        node.classList?.contains('product-title-sca');
                });
            });

            if (relevantMutation) {
                console.log('🔄 Relevant change detected via mutation observer');
                // Use requestAnimationFrame for smoother processing
                requestAnimationFrame(() => {
                    this.processAddon();
                });
            }
        }

        async processAddon() {
            // Prevent concurrent processing
            if (!stateManager.startProcessing()) {
                console.log('⏸ Already processing, skipping');
                return;
            }

            try {
                console.log('🔍 Processing addon state...');

                // Use a more robust element detection with retry mechanism
                const elements = await this._detectElements();
                const { cartAddon, addOnTitle } = elements;

                if (!cartAddon && !addOnTitle) {
                    console.log('📭 No addon elements found');
                    stateManager.state.addonPresent = false;
                    return;
                }

                stateManager.state.addonPresent = true;

                // Add info button if title exists and button is missing
                if (addOnTitle) {
                    const existingButton = addOnTitle.querySelector(`#${this.button.buttonId}`);
                    if (!existingButton) {
                        console.log('🔍 Button missing, adding to:', addOnTitle.className || 'title element');
                        this.button.add(addOnTitle);
                        // Set up monitoring for this button
                        this._monitorButton(addOnTitle);
                    } else {
                        console.log('✓ Button already exists in title element');
                    }
                } else {
                    console.log('⚠ No title element found for button');
                }

                // Check insurance checkbox immediately if cart addon exists
                if (cartAddon) {
                    // Use requestAnimationFrame for immediate but smooth processing
                    requestAnimationFrame(() => {
                        // Only check the checkbox if we're still processing
                        if (stateManager.state.isProcessing) {
                            InsuranceCheckbox.check();
                        }
                    });
                }

                // Ensure modal is initialized
                if (!this.modal.isInitialized) {
                    await this.modal.initialize();
                }

            } finally {
                stateManager.stopProcessing();
            }
        }

        _monitorButton(titleElement) {
            // Only set up monitoring if not already monitoring
            if (titleElement._buttonObserver) {
                return;
            }

            let isReadding = false; // Prevent multiple simultaneous re-adds

            // Monitor for button removal
            const observer = new MutationObserver((mutations) => {
                if (isReadding) return; // Prevent multiple simultaneous re-adds

                mutations.forEach((mutation) => {
                    mutation.removedNodes.forEach((node) => {
                        if (node.id === this.button.buttonId ||
                            (node.nodeType === Node.ELEMENT_NODE && node.querySelector(`#${this.button.buttonId}`))) {
                            console.log('🚨 Button was removed! Re-adding...');
                            isReadding = true;

                            // Use a longer delay to let any other DOM updates complete
                            setTimeout(() => {
                                if (!titleElement.querySelector(`#${this.button.buttonId}`)) {
                                    this.button.add(titleElement);
                                }
                                isReadding = false;
                            }, 200); // Increased delay to prevent rapid re-adds
                        }
                    });
                });
            });

            observer.observe(titleElement, {
                childList: true,
                subtree: true
            });

            // Store observer for cleanup
            titleElement._buttonObserver = observer;
        }

        async _detectElements() {
            // Try immediate detection first
            let cartAddon = DOMUtils.querySelector(CONFIG.selectors.cartAddon);
            let addOnTitle = DOMUtils.querySelector(CONFIG.selectors.addOnTitle);

            // If elements not found immediately, wait briefly for DOM updates
            if (!cartAddon && !addOnTitle) {
                await new Promise(resolve => requestAnimationFrame(resolve));
                cartAddon = DOMUtils.querySelector(CONFIG.selectors.cartAddon);
                addOnTitle = DOMUtils.querySelector(CONFIG.selectors.addOnTitle);
            }

            return { cartAddon, addOnTitle };
        }

        destroy() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
            stateManager.state.hasInitialized = false;
            console.log('🛑 Insurance Add-on Manager destroyed');
        }
    }

    // ==========================================
    // Initialization
    // ==========================================
    const manager = new InsuranceAddonManager();

    // Smart initialization strategy
    const initializeWhenReady = () => {
        // Check if Rebuy cart exists
        const cartExists = DOMUtils.querySelector(CONFIG.selectors.smartCart) ||
            DOMUtils.querySelector(CONFIG.selectors.cartAddon);

        if (cartExists) {
            manager.initialize();
            return true;
        }
        return false;
    };

    // Listen for Rebuy events
    ['rebuy:smartcart.ready', 'rebuy:smartcart.show'].forEach(eventName => {
        document.addEventListener(eventName, () => {
            console.log(`📢 ${eventName} event fired`);
            setTimeout(() => manager.initialize(), CONFIG.delays.initialization);
        });
    });

    // Add debouncing for rebuy cart change events
    let rebuyChangeTimeout;
    document.addEventListener('rebuy:cart.change', () => {
        console.log('rebuy:cart.change event fired');
        // Clear any pending timeout
        if (rebuyChangeTimeout) {
            clearTimeout(rebuyChangeTimeout);
        }
        // Small delay to let Rebuy finish its DOM updates before we process
        rebuyChangeTimeout = setTimeout(() => {
            manager.processAddon();
        }, 100); // Small delay to let Rebuy complete its updates
    })

    // Check on DOMContentLoaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeWhenReady);
    } else {
        // DOM already loaded
        initializeWhenReady();
    }

    // Fallback: Check periodically for the first 5 seconds
    let attempts = 0;
    const maxAttempts = 10;
    const checkInterval = setInterval(() => {
        attempts++;

        if (initializeWhenReady() || attempts >= maxAttempts) {
            clearInterval(checkInterval);
            if (attempts >= maxAttempts) {
                console.log('⏱ Max initialization attempts reached');
            }
        }
    }, 500);

    // ==========================================
    // Debug Utilities (for development)
    // ==========================================
    window.insuranceAddon = {
        reinitialize: () => {
            manager.destroy();
            setTimeout(() => manager.initialize(), 100);
        },
        destroy: () => manager.destroy(),
        processNow: () => manager.processAddon(),
        getState: () => stateManager.state,
        showModal: () => manager.modal.show(),
        hideModal: () => manager.modal.hide()
    };

    console.log('💉 Insurance Add-on Manager injected');

})();